/**
 * useResetCountdown (WS 1.2, Decision 4)
 * ========================================
 * Live HH:MM:SS countdown to the next DEMO daily-quota reset (UTC midnight,
 * or the backend-provided resetAtUtc when known). Ticks every second via
 * setInterval so the usage meter shows a genuinely live countdown rather than
 * whole hours.
 *
 * `computeCountdown` is exported as a pure function so it can be unit-tested
 * deterministically with fake timers, without needing @testing-library/preact
 * (not installed on this branch) or renderHook.
 */
import { useEffect, useState } from 'preact/hooks'
import { resetAtUtc } from '../state/demoLimitStore.js'

function targetMs(reset) {
  if (reset) {
    const t = new Date(reset).getTime()
    if (Number.isFinite(t)) return t
  }
  // Fall back to the next UTC midnight when resetAtUtc is unknown.
  const d = new Date()
  d.setUTCHours(24, 0, 0, 0)
  return d.getTime()
}

/**
 * Pure: compute {hh, mm, ss, formatted, expired} for a given reset timestamp
 * (ISO string or null/undefined) relative to Date.now().
 * @param {string|null|undefined} reset - ISO reset timestamp, or null to fall back to next UTC midnight
 */
export function computeCountdown(reset) {
  const ms = Math.max(0, targetMs(reset) - Date.now())
  const total = Math.floor(ms / 1000)
  const hh = Math.floor(total / 3600)
  const mm = Math.floor((total % 3600) / 60)
  const ss = total % 60
  const pad = (n) => String(n).padStart(2, '0')
  return { hh, mm, ss, formatted: `${pad(hh)}:${pad(mm)}:${pad(ss)}`, expired: ms <= 0 }
}

const IDLE = { hh: 0, mm: 0, ss: 0, formatted: '00:00:00', expired: false }

/**
 * Live countdown hook: { hh, mm, ss, formatted, expired }. Reads resetAtUtc
 * from demoLimitStore and re-renders every second while mounted.
 *
 * @param {boolean} [enabled=true] - when false, the 1s interval is skipped
 *   entirely and an idle snapshot is returned. Callers (e.g. DemoUsageMeter)
 *   must still call this hook unconditionally on every render — Rules of
 *   Hooks — even when the meter itself is hidden for non-DEMO tiers; `enabled`
 *   is how a hidden meter avoids ticking a second-by-second interval forever
 *   for every non-demo user.
 */
export function useResetCountdown(enabled = true) {
  const reset = resetAtUtc.value
  const [state, setState] = useState(() => (enabled ? computeCountdown(reset) : IDLE))

  useEffect(() => {
    if (!enabled) {
      setState(IDLE)
      return
    }
    setState(computeCountdown(reset))
    const id = setInterval(() => setState(computeCountdown(reset)), 1000)
    return () => clearInterval(id)
  }, [enabled, reset])

  return state
}
